import type { TradeLogicRegistryActionTemplateDto } from '../registry';

export type AeroParameterFamilySupportKey =
    | 'maxHoldBars'
    | 'profitFloor'
    | 'profitTarget'
    | 'profitTargetAutoPartialExits'
    | 'profitTargetPartialExits'
    | 'stopLoss';

export interface AeroParameterFamilySupport {
    key: AeroParameterFamilySupportKey;
    available: boolean;
    label: string;
    missingReason: string | null;
    requiredActionKinds: string[];
}

export interface AeroParameterSpaceRegistryPlan {
    actionTemplateCount: number;
    fullExitTemplate: TradeLogicRegistryActionTemplateDto | null;
    isLoading: boolean;
    partialExitTemplate: TradeLogicRegistryActionTemplateDto | null;
    supports: Record<AeroParameterFamilySupportKey, AeroParameterFamilySupport>;
}

const FULL_EXIT_ACTION_KINDS = new Set(['exit', 'fullExit']);
const PARTIAL_EXIT_ACTION_KINDS = new Set(['partialExit']);

const FAMILY_LABELS: Record<AeroParameterFamilySupportKey, string> = {
    maxHoldBars: 'Maximum Hold Bars',
    profitFloor: 'Profit Floors',
    profitTarget: 'Profit Targets',
    profitTargetAutoPartialExits: 'Auto Partial Exits',
    profitTargetPartialExits: 'Manual Partial Exits',
    stopLoss: 'Stop Loss'
};

export const buildAeroParameterSpaceRegistryPlan = ({
    actionTemplates,
    isLoading = false
}: {
    actionTemplates: TradeLogicRegistryActionTemplateDto[];
    isLoading?: boolean;
}): AeroParameterSpaceRegistryPlan => {
    const fullExitTemplate = findActionTemplateByKinds(actionTemplates, FULL_EXIT_ACTION_KINDS);
    const partialExitTemplate = findActionTemplateByKinds(actionTemplates, PARTIAL_EXIT_ACTION_KINDS);
    const loadingReason = isLoading ? 'Registry action templates are still loading.' : null;

    return {
        actionTemplateCount: actionTemplates.length,
        fullExitTemplate,
        isLoading,
        partialExitTemplate,
        supports: {
            maxHoldBars: buildFamilySupport({
                available: fullExitTemplate !== null && !isLoading,
                key: 'maxHoldBars',
                loadingReason,
                requiredActionKinds: ['exit']
            }),
            profitFloor: buildFamilySupport({
                available: fullExitTemplate !== null && !isLoading,
                key: 'profitFloor',
                loadingReason,
                requiredActionKinds: ['exit']
            }),
            profitTarget: buildFamilySupport({
                available: fullExitTemplate !== null && !isLoading,
                key: 'profitTarget',
                loadingReason,
                requiredActionKinds: ['exit']
            }),
            profitTargetAutoPartialExits: buildFamilySupport({
                available: fullExitTemplate !== null && partialExitTemplate !== null && !isLoading,
                key: 'profitTargetAutoPartialExits',
                loadingReason,
                requiredActionKinds: ['exit', 'partialExit']
            }),
            profitTargetPartialExits: buildFamilySupport({
                available: fullExitTemplate !== null && partialExitTemplate !== null && !isLoading,
                key: 'profitTargetPartialExits',
                loadingReason,
                requiredActionKinds: ['exit', 'partialExit']
            }),
            stopLoss: buildFamilySupport({
                available: fullExitTemplate !== null && !isLoading,
                key: 'stopLoss',
                loadingReason,
                requiredActionKinds: ['exit']
            })
        }
    };
};

export const getAeroParameterFamilySupport = (
    registryPlan: AeroParameterSpaceRegistryPlan | null | undefined,
    key: AeroParameterFamilySupportKey
) => registryPlan?.supports[key] ?? null;

export const isAeroParameterFamilyAvailable = (
    registryPlan: AeroParameterSpaceRegistryPlan | null | undefined,
    key: AeroParameterFamilySupportKey
) => registryPlan?.supports[key]?.available ?? true;

export const buildAeroParameterRegistryBlockingReasons = ({
    activeFamilyKeys,
    registryPlan
}: {
    activeFamilyKeys: AeroParameterFamilySupportKey[];
    registryPlan: AeroParameterSpaceRegistryPlan | null | undefined;
}) => {
    if (!registryPlan) return [];

    return activeFamilyKeys
        .map((familyKey) => registryPlan.supports[familyKey].missingReason)
        .filter((reason): reason is string => reason !== null);
};

export const getAeroPartialExitTemplateDefaultExitPercentage = (registryPlan: AeroParameterSpaceRegistryPlan | null | undefined) => {
    const quantityFraction = registryPlan?.partialExitTemplate?.defaults?.quantityFraction;

    return typeof quantityFraction === 'number' && Number.isFinite(quantityFraction) ? quantityFraction * 100 : null;
};

const buildFamilySupport = ({
    available,
    key,
    loadingReason,
    requiredActionKinds
}: {
    available: boolean;
    key: AeroParameterFamilySupportKey;
    loadingReason: string | null;
    requiredActionKinds: string[];
}): AeroParameterFamilySupport => ({
    available,
    key,
    label: FAMILY_LABELS[key],
    missingReason: available ? null : (loadingReason ?? `${FAMILY_LABELS[key]} require ${formatRequiredActionKinds(requiredActionKinds)}.`),
    requiredActionKinds
});

const findActionTemplateByKinds = (actionTemplates: TradeLogicRegistryActionTemplateDto[], actionKinds: Set<string>) =>
    actionTemplates.find((template) => actionKinds.has(getCanonicalActionKind(template))) ?? null;

const getCanonicalActionKind = (template: TradeLogicRegistryActionTemplateDto) => {
    const canonicalAction = template.metadata?.canonicalAction;

    return typeof canonicalAction === 'string' ? canonicalAction : (template.actionKind ?? '');
};

const formatRequiredActionKinds = (requiredActionKinds: string[]) => {
    if (requiredActionKinds.length === 1) return `${requiredActionKinds[0]} action template support`;

    const [firstKind, ...remainingKinds] = requiredActionKinds;

    return `${firstKind} and ${remainingKinds.join(' and ')} action template support`;
};
